import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, writeBatch, query, where } from 'firebase/firestore';
import { db } from './firebase';
import styles from './ProductionDashboard.module.css';

const ProductionDashboard = () => {
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOrderList, setShowOrderList] = useState(false);
  const [combinedIngredients, setCombinedIngredients] = useState([]);
  const [shortfallItems, setShortfallItems] = useState([]);
  const [productionDate, setProductionDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Unit conversion factors (to grams for weight, to ml for volume)
  const unitConversions = useMemo(() => ({
    // Weight conversions to grams
    'kg': 1000,
    'g': 1,
    'oz': 28.35,
    'lb': 453.59,
    'lbs': 453.59,
    // Volume conversions to ml
    'l': 1000,
    'liters': 1000,
    'ml': 1,
    'cup': 240,
    'cups': 236.588,
    'tbsp': 15,
    'tsp': 5,
    'fl oz': 29.57,
    // Count
    'pcs': 1,
    'units': 1,
    'pieces': 1,
    'boxes': 1,
    'packets': 1,
    'bottles': 1,
    'cans': 1,
    'bags': 1
  }), []);

  // Unit categories for conversion compatibility
  const unitCategories = useMemo(() => ({
    weight: ['g', 'kg', 'lbs', 'lb', 'oz'],
    volume: ['ml', 'liters', 'l', 'cups', 'cup', 'tbsp', 'tsp', 'fl oz'],
    count: ['units', 'pieces', 'pcs', 'boxes', 'packets', 'bottles', 'cans', 'bags']
  }), []);

  // Function to get the category of a unit
  const getUnitCategory = useCallback((unit) => {
    for (const [category, units] of Object.entries(unitCategories)) {
      if (units.includes(unit.toLowerCase())) return category;
    }
    return 'count'; // Default to count if unknown
  }, [unitCategories]);

  // Function to automatically convert to a better unit for display
  const getBestUnit = useCallback((value, unit) => {
    const category = getUnitCategory(unit);
    if (category === 'count') return { value: Math.round(value * 100) / 100, unit };
    
    // Convert to base unit first
    const baseValue = value * (unitConversions[unit.toLowerCase()] || 1);
    
    // Define preferred units in order of preference for each category
    const preferredUnits = {
      weight: [
        { unit: 'kg', threshold: 1000 },    // Use kg for >= 1000g
        { unit: 'lbs', threshold: 453.592 }, // Use lbs for >= 453.592g (1 lb)
        { unit: 'g', threshold: 0 }         // Use g for everything else
      ],
      volume: [
        { unit: 'liters', threshold: 1000 }, // Use liters for >= 1000ml
        { unit: 'cups', threshold: 236.588 }, // Use cups for >= 236.588ml
        { unit: 'ml', threshold: 0 }         // Use ml for everything else
      ]
    };
    
    const units = preferredUnits[category] || [{ unit: unit, threshold: 0 }];
    
    // Find the best unit (largest unit where value >= threshold)
    for (const { unit: testUnit, threshold } of units) {
      if (baseValue >= threshold) {
        const convertedValue = baseValue / (unitConversions[testUnit.toLowerCase()] || 1);
        return { 
          value: Math.round(convertedValue * 100) / 100, 
          unit: testUnit 
        };
      }
    }
    
    // Fallback to original
    return { value: Math.round(value * 100) / 100, unit };
  }, [unitConversions, getUnitCategory]);

  const convertUnits = useCallback((amount, fromUnit, toUnit) => {
    if (!fromUnit || !toUnit || fromUnit === toUnit) return amount;

    const fromFactor = unitConversions[fromUnit.toLowerCase()];
    const toFactor = unitConversions[toUnit.toLowerCase()];

    if (!fromFactor || !toFactor) return amount;

    // Convert to base unit, then to target unit
    const baseAmount = amount * fromFactor;
    return baseAmount / toFactor;
  }, [unitConversions]);

  useEffect(() => {
    fetchData();
  }, []);

  const calculateCombinedIngredients = useCallback(() => {
    const combined = {};
    const shortfall = [];

    // Combine all ingredients from selected recipes
    selectedRecipes.forEach(recipe => {
      if (recipe.recipe && Array.isArray(recipe.recipe)) {
        recipe.recipe.forEach(recipeIngredient => {
          const ingredientName = String(recipeIngredient.name || "").trim().toLowerCase();
          if (ingredientName === "water") return;

          const ingredientId = recipeIngredient.ingredientId;
          const basePackets = Number.isFinite(recipe.basePackets) && recipe.basePackets > 0 ? recipe.basePackets : 1;
          const requiredAmount = ((recipeIngredient.quantity || 0) / basePackets) * recipe.quantity;
          
          if (combined[ingredientId]) {
            const recipeUnit = recipeIngredient.unit || combined[ingredientId].recipeUnit || combined[ingredientId].unit || 'units';
            const stockUnit = combined[ingredientId].unit || recipeUnit;
            const requiredInStockUnit = convertUnits(requiredAmount, recipeUnit, stockUnit);
            combined[ingredientId].totalRequired += requiredInStockUnit;
          } else {
            const ingredientData = ingredients.find(ing => ing.id === ingredientId);
            const inventoryName = String(ingredientData?.name || recipeIngredient.name || "").trim().toLowerCase();
            if (inventoryName === "water") return;

            const recipeUnit = recipeIngredient.unit || ingredientData?.unit || 'units';
            const stockUnit = ingredientData?.unit || recipeUnit || 'units';
            const requiredInStockUnit = convertUnits(requiredAmount, recipeUnit, stockUnit);
            combined[ingredientId] = {
              id: ingredientId,
              name: recipeIngredient.name || ingredientData?.name || 'Unknown Ingredient',
              unit: stockUnit,
              recipeUnit,
              totalRequired: requiredInStockUnit,
              currentStock: ingredientData?.currentStock ?? ingredientData?.stock ?? 0,
              cost: ingredientData?.cost || 0
            };
          }
        });
      }
    });

    // Calculate shortfalls with intelligent unit conversion
    Object.values(combined).forEach(ingredient => {
      const shortfallAmount = ingredient.totalRequired - ingredient.currentStock;
      if (shortfallAmount > 0) {
        const bestUnit = getBestUnit(shortfallAmount, ingredient.unit);
        shortfall.push({
          ...ingredient,
          shortfallAmount: bestUnit.value,
          shortfallUnit: bestUnit.unit,
          estimatedCost: shortfallAmount * ingredient.cost
        });
      }
    });

    // Apply intelligent unit conversion to combined ingredients for display
    const displayCombined = Object.values(combined).map(ingredient => {
      const requiredBest = getBestUnit(ingredient.totalRequired, ingredient.unit);
      const stockBest = getBestUnit(ingredient.currentStock, ingredient.unit);
      
      return {
        ...ingredient,
        displayRequired: requiredBest.value,
        displayRequiredUnit: requiredBest.unit,
        displayStock: stockBest.value,
        displayStockUnit: stockBest.unit
      };
    });

    setCombinedIngredients(displayCombined);
    setShortfallItems(shortfall);
  }, [selectedRecipes, ingredients, getBestUnit, convertUnits]);

  useEffect(() => {
    calculateCombinedIngredients();
  }, [selectedRecipes, ingredients, calculateCombinedIngredients]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch products
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Fetch ingredients
      const ingredientsSnapshot = await getDocs(collection(db, 'ingredients'));
      const ingredientsData = ingredientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch inventory (assuming ingredients and inventory are the same collection)
      const inventoryData = ingredientsData.map(ingredient => ({
        ...ingredient,
        name: ingredient.name,
        stock: String(ingredient.name || "").trim().toLowerCase() === "water"
          ? Number.POSITIVE_INFINITY
          : (ingredient.currentStock ?? ingredient.stock ?? 0),
        unit: ingredient.unit || 'units'
      }));
      
      setProducts(productsData);
      setIngredients(ingredientsData);
      setInventory(inventoryData);
      
      console.log('Products loaded:', productsData.length);
      console.log('Products with recipes:', productsData.filter(p => p.recipes && p.recipes.length > 0).length);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get recipe from product (handles different field names)
  const getProductRecipe = (product) => {
    // Handle the actual schema: products.recipes[0].ingredients
    if (product.recipes && Array.isArray(product.recipes) && product.recipes.length > 0) {
      return product.recipes[0].ingredients || [];
    }
    // Fallback to other possible field names
    return product.recipe || product.ingredients || product.Recipe || [];
  };

  const getRecipeBasePackets = (product) => {
    if (product?.recipes && Array.isArray(product.recipes) && product.recipes.length > 0) {
      const base = product.recipes[0].basePackets;
      if (Number.isFinite(base) && base > 0) return base;
    }
    const fallback = product?.basePackets;
    if (Number.isFinite(fallback) && fallback > 0) return fallback;
    return 1;
  };

  // Check if a recipe can be made with current stock
  const canMakeRecipe = (recipe, quantity = 1, basePackets = 1) => {
    if (!recipe || recipe.length === 0) return true;
    
    return recipe.every(ingredient => {
      if (String(ingredient.name || "").trim().toLowerCase() === "water") return true;
      const inventoryItem = inventory.find(item => item.name === ingredient.name);
      if (!inventoryItem) return false;
      
      // Convert units if needed for comparison
      const requiredQuantity = (ingredient.quantity / basePackets) * quantity;
      const convertedQuantity = convertUnits(requiredQuantity, ingredient.unit, inventoryItem.unit);
      return inventoryItem.stock >= convertedQuantity;
    });
  };

  const addRecipeToProduction = (product, quantity = 1) => {
    const existingIndex = selectedRecipes.findIndex(recipe => recipe.productId === product.id);
    const basePackets = getRecipeBasePackets(product);
    const recipeIngredients = getProductRecipe(product);
    
    if (existingIndex >= 0) {
      // Update quantity if recipe already exists
      const updatedRecipes = [...selectedRecipes];
      updatedRecipes[existingIndex].quantity += quantity;
      setSelectedRecipes(updatedRecipes);
    } else {
      // Add new recipe
      setSelectedRecipes([...selectedRecipes, {
        productId: product.id,
        productName: product.name,
        recipe: recipeIngredients,
        basePackets,
        quantity: quantity
      }]);
    }
  };

  const updateRecipeQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeRecipeFromProduction(productId);
      return;
    }
    
    setSelectedRecipes(prev => 
      prev.map(recipe => 
        recipe.productId === productId 
          ? { ...recipe, quantity: newQuantity }
          : recipe
      )
    );
  };

  const removeRecipeFromProduction = (productId) => {
    setSelectedRecipes(prev => prev.filter(recipe => recipe.productId !== productId));
  };

  const executeProduction = async () => {
    try {
      const eventDate = productionDate ? new Date(`${productionDate}T12:00:00`) : new Date();
      const yearMonth = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, "0")}`;
      const monthLabel = eventDate.toLocaleString('en-US', { month: 'short' });
      const batch = writeBatch(db);

      // Maps for quick lookups and counters
      const ingredientById = new Map(ingredients.map(i => [i.id, i]));
      const ingredientIds = combinedIngredients.map(ing => ing.id).filter(Boolean);
      const productIds = Array.from(new Set(selectedRecipes.map(r => r.productId))).filter(Boolean);

      // Load lots for each ingredient used in this production run
      const lotSnaps = await Promise.all(
        ingredientIds.map((id) =>
          getDocs(query(collection(db, 'ingredient_inventory_lots'), where('ingredientId', '==', id)))
        )
      );

      const lotsByIngredientId = new Map();
      lotSnaps.forEach((snap, idx) => {
        const ingId = ingredientIds[idx];
        const lots = snap.docs
          .map(d => ({
            id: d.id,
            ref: d.ref,
            ...d.data(),
            remainingQuantity: Number(d.data()?.remainingQuantity || 0),
            purchaseDate: d.data()?.purchaseDate || null
          }))
          .sort((a, b) => {
            const da = a?.purchaseDate?.seconds
              ? a.purchaseDate.seconds
              : (a?.purchaseDate instanceof Date ? Math.floor(a.purchaseDate.getTime() / 1000) : 0);
            const dbb = b?.purchaseDate?.seconds
              ? b.purchaseDate.seconds
              : (b?.purchaseDate instanceof Date ? Math.floor(b.purchaseDate.getTime() / 1000) : 0);
            return da - dbb;
          });

        lotsByIngredientId.set(ingId, lots);
      });

      // Ensure lots align with current stock by top-upping an existing legacy balance lot if needed
      for (const ingId of ingredientIds) {
        const ing = ingredientById.get(ingId);
        const lots = lotsByIngredientId.get(ingId) || [];
        const currentStock = Number(ing?.currentStock ?? ing?.stock ?? 0) || 0;
        const totalRemaining = lots.reduce((sum, l) => sum + (Number(l.remainingQuantity) || 0), 0);
        const missingBalance = Math.max(0, Math.round((currentStock - totalRemaining) * 1000) / 1000);

        if (missingBalance > 0) {
          const isEpoch = (pd) => {
            if (!pd) return false;
            if (pd instanceof Date) return pd.getTime() === 0;
            if (typeof pd === 'object' && typeof pd.seconds === 'number') return pd.seconds === 0;
            return false;
          };

          const existingLegacy = lots.find(l => l?.isLegacy && isEpoch(l?.purchaseDate) && !l?.purchaseHistoryId);
          if (existingLegacy?.ref) {
            const prevPurchased = Number(existingLegacy.quantityPurchased ?? existingLegacy.remainingQuantity ?? 0) || 0;
            const prevRemaining = Number(existingLegacy.remainingQuantity || 0) || 0;
            const nextPurchased = Math.round((prevPurchased + missingBalance) * 1000) / 1000;
            const nextRemaining = Math.round((prevRemaining + missingBalance) * 1000) / 1000;

            existingLegacy.quantityPurchased = nextPurchased;
            existingLegacy.remainingQuantity = nextRemaining;
            batch.update(existingLegacy.ref, {
              quantityPurchased: nextPurchased,
              remainingQuantity: nextRemaining,
              updatedAt: eventDate
            });
          } else {
            const legacyRef = doc(collection(db, 'ingredient_inventory_lots'));
            const legacyLot = {
              ingredientId: ingId,
              ingredientName: ing?.name || null,
              unit: ing?.unit || 'units',
              purchaseDate: new Date(0),
              quantityPurchased: missingBalance,
              remainingQuantity: missingBalance,
              isLegacy: true,
              createdAt: eventDate,
              updatedAt: eventDate
            };
            batch.set(legacyRef, legacyLot);
            lots.unshift({ id: legacyRef.id, ref: legacyRef, ...legacyLot });
            lotsByIngredientId.set(ingId, lots);
          }
        }
      }

      // Merge multiple legacy epoch lots into one per ingredient (if present).
      // This prevents one production event from being split across many legacy lots.
      for (const ingId of ingredientIds) {
        const lots = lotsByIngredientId.get(ingId) || [];
        const isEpoch = (pd) => {
          if (!pd) return false;
          if (pd instanceof Date) return pd.getTime() === 0;
          if (typeof pd === 'object' && typeof pd.seconds === 'number') return pd.seconds === 0;
          return false;
        };

        const legacyLots = lots.filter(l => l?.isLegacy && isEpoch(l?.purchaseDate) && !l?.purchaseHistoryId);
        if (legacyLots.length <= 1) continue;

        const primary = legacyLots[0];
        let mergedRemaining = Number(primary.remainingQuantity || 0) || 0;
        for (const extra of legacyLots.slice(1)) {
          const extraRemaining = Number(extra.remainingQuantity || 0) || 0;
          if (extraRemaining > 0 && extra?.ref) {
            mergedRemaining = Math.round((mergedRemaining + extraRemaining) * 1000) / 1000;
            extra.remainingQuantity = 0;
            batch.update(extra.ref, { remainingQuantity: 0, updatedAt: eventDate });
          }
        }

        if (primary?.ref) {
          primary.remainingQuantity = mergedRemaining;
          batch.update(primary.ref, { remainingQuantity: mergedRemaining, updatedAt: eventDate });
        }
      }

      // Precompute existing event counts per product (for batch codes)
      const eventSnaps = await Promise.all(
        productIds.map((pid) =>
          getDocs(query(collection(db, 'productionEvents'), where('productId', '==', pid)))
        )
      );

      const seqCounters = new Map();
      eventSnaps.forEach((snap, idx) => {
        const pid = productIds[idx];
        const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const existingCount = events.filter(evt => {
          const dt = evt?.date?.seconds
            ? new Date(evt.date.seconds * 1000)
            : (evt?.date instanceof Date ? evt.date : null);
          const ym = evt?.yearMonth || (dt ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}` : null);
          return ym === yearMonth;
        }).length;
        seqCounters.set(pid, existingCount);
      });

      // Track lot updates and ingredient consumption
      const touchedLots = new Map(); // lotId -> lot object with updated remainingQuantity

      for (const selected of selectedRecipes) {
        const product = products.find(p => p.id === selected.productId) || {};
        const recipeIngredients = getProductRecipe(product) || [];
        const basePackets = getRecipeBasePackets(product);
        const recipeName = (product.recipes && product.recipes[0]?.recipeName) || product.recipeName || null;

        const currentSeq = seqCounters.get(selected.productId) || 0;
        const nextSeq = currentSeq + 1;
        seqCounters.set(selected.productId, nextSeq);
        const batchCode = `${selected.productName}-${monthLabel}-${nextSeq}`;

        const productionEventRef = doc(collection(db, 'productionEvents'));

        const eventIngredients = recipeIngredients
          .filter(ing => String(ing?.name || "").trim().toLowerCase() !== "water")
          .map(ing => {
            const ingRecord = ingredientById.get(ing.ingredientId);
            const recipeUnit = ing.unit || 'units';
            const stockUnit = ingRecord?.unit || recipeUnit;
            const qtyRecipeUnit = ((ing.quantity || 0) / basePackets) * (selected.quantity || 1);
            const qtyStockUnit = convertUnits(qtyRecipeUnit, recipeUnit, stockUnit);
            return {
              ingredientId: ingRecord?.id || ing.ingredientId || null,
              name: ing.name || ingRecord?.name || '-',
              quantity: qtyRecipeUnit,
              unit: recipeUnit,
              quantityStockUnit: qtyStockUnit,
              stockUnit
            };
          });

        // Allocate FIFO lots and create usage rows per ingredient
        for (const ing of eventIngredients) {
          let allocSeq = 0;
          if (!ing.ingredientId) continue;
          const lots = lotsByIngredientId.get(ing.ingredientId) || [];
          let remaining = Math.round((Number(ing.quantityStockUnit) || 0) * 1000) / 1000;

          for (const lot of lots) {
            if (remaining <= 0) break;
            const lotRemaining = Number(lot.remainingQuantity) || 0;
            if (lotRemaining <= 0) continue;

            const used = Math.min(lotRemaining, remaining);
            const newLotRemaining = Math.round((lotRemaining - used) * 1000) / 1000;
            lot.remainingQuantity = newLotRemaining;
            remaining = Math.round((remaining - used) * 1000) / 1000;

            touchedLots.set(lot.id, lot);

            const usageRef = doc(
              db,
              'ingredient_usage_history',
              `${productionEventRef.id}-${ing.ingredientId}-${lot.id}-${allocSeq}`
            );
            allocSeq += 1;
            batch.set(usageRef, {
              ingredientId: ing.ingredientId,
              ingredientName: ing.name,
              unit: ing.stockUnit,
              quantityUsed: used,
              date: eventDate,
              productId: selected.productId,
              productName: selected.productName,
              recipeName,
              quantityProduced: selected.quantity || 1,
              productionEventId: productionEventRef.id,
              batchCode,
              yearMonth,
              lotId: lot.id,
              lotPurchaseDate: lot.purchaseDate || null,
              purchaseHistoryId: lot.purchaseHistoryId || null,
              lotRemainingAfter: newLotRemaining,
              createdAt: eventDate
            });
          }

          if (remaining > 0) {
            console.warn(`FIFO allocation shortfall for ${ing.name}: missing ${remaining} ${ing.stockUnit}`);
          }
        }

        batch.set(productionEventRef, {
          productId: selected.productId,
          productName: selected.productName,
          recipeName,
          quantityProduced: selected.quantity || 1,
          date: eventDate,
          yearMonth,
          batchCode,
          ingredients: eventIngredients,
          createdAt: eventDate,
          fifoMigrated: true
        });
      }

      // Persist lot updates
      for (const lot of touchedLots.values()) {
        if (lot?.ref) {
          batch.update(lot.ref, {
            remainingQuantity: lot.remainingQuantity,
            updatedAt: eventDate
          });
        }
      }

      // Update ingredient stocks to reflect remaining lot quantities
      for (const ingId of ingredientIds) {
        const lots = lotsByIngredientId.get(ingId) || [];
        const totalRemaining = lots.reduce((sum, l) => sum + (Number(l.remainingQuantity) || 0), 0);
        batch.update(doc(db, 'ingredients', ingId), {
          currentStock: Math.max(0, Math.round(totalRemaining * 1000) / 1000),
          lastUpdated: eventDate
        });
      }

      await batch.commit();

      alert('Production completed successfully!');
      setSelectedRecipes([]);
      fetchData();
    } catch (error) {
      console.error('Error executing production:', error);
      alert('Error executing production. Please try again.');
    }
  };

  const canExecuteProduction = () => {
    return shortfallItems.length === 0 && selectedRecipes.length > 0;
  };

  if (loading) {
    return <div className={styles.loading}>Loading production data...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Production Dashboard</h2>
        <div className={styles.headerActions}>
          <div>
            <label style={{ display: 'block', fontSize: 12, opacity: 0.8 }}>Production Date</label>
            <input
              type="date"
              value={productionDate}
              onChange={(e) => setProductionDate(e.target.value)}
            />
          </div>
          <button 
            className={styles.orderButton}
            onClick={() => setShowOrderList(true)}
            disabled={shortfallItems.length === 0}
          >
            View Order List ({shortfallItems.length} items)
          </button>
          <button 
            className={`${styles.executeButton} ${canExecuteProduction() ? styles.ready : styles.disabled}`}
            onClick={executeProduction}
            disabled={!canExecuteProduction()}
          >
            Execute Production
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {/* Available Recipes */}
        <div className={styles.section}>
          <h3>Available Recipes</h3>
          <div className={styles.recipesGrid}>
            {loading ? (
              <div className={styles.loadingMessage}>Loading recipes...</div>
            ) : products.length === 0 ? (
              <div className={styles.emptyMessage}>No products found</div>
            ) : products.filter(product => {
              const recipe = getProductRecipe(product);
              return recipe && recipe.length > 0;
            }).length === 0 ? (
              <div className={styles.emptyMessage}>
                No products with recipes found. 
                <br />
                <small>Add recipes to products to use the production dashboard.</small>
              </div>
            ) : (
              products.filter(product => {
                const recipe = getProductRecipe(product);
                return recipe && recipe.length > 0;
              }).map(product => {
                const recipe = getProductRecipe(product);
                const basePackets = getRecipeBasePackets(product);
                const canMake = canMakeRecipe(recipe, 1, basePackets);
                
                return (
                  <div key={product.id} className={`${styles.recipeCard} ${canMake ? styles.canMake : styles.needsIngredients}`}>
                    <div className={styles.recipeHeader}>
                      <h4>{product.name}</h4>
                      <div className={`${styles.stockStatus} ${canMake ? styles.inStock : styles.outOfStock}`}>
                        {canMake ? '✓ Can Make' : '⚠ Needs Ingredients'}
                      </div>
                    </div>
                    <p>{recipe.length} ingredients</p>
                    
                    <button 
                      className={styles.addButton}
                      onClick={() => addRecipeToProduction(product)}
                    >
                      Add to Production
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Selected Recipes */}
        {selectedRecipes.length > 0 && (
          <div className={styles.section}>
            <h3>Selected Recipes</h3>
            <div className={styles.selectedRecipes}>
              {selectedRecipes.map(recipe => (
                <div key={recipe.productId} className={styles.selectedRecipe}>
                  <div className={styles.recipeInfo}>
                    <h4>{recipe.productName}</h4>
                    <div className={styles.quantityControl}>
                      <label>Packets:</label>
                      <input
                        type="number"
                        min="1"
                        value={recipe.quantity}
                        onChange={(e) => updateRecipeQuantity(recipe.productId, parseInt(e.target.value) || 1)}
                        className={styles.quantityInput}
                      />
                    </div>
                  </div>
                  <button 
                    className={styles.removeButton}
                    onClick={() => removeRecipeFromProduction(recipe.productId)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Combined Ingredients */}
        {combinedIngredients.length > 0 && (
          <div className={styles.section}>
            <h3>Required Ingredients</h3>
            <div className={styles.ingredientsTable}>
              <table>
                <thead>
                  <tr>
                    <th>Ingredient</th>
                    <th>Required</th>
                    <th>In Stock</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {combinedIngredients.map(ingredient => {
                    const isShort = ingredient.totalRequired > ingredient.currentStock;
                    return (
                      <tr key={ingredient.id} className={isShort ? styles.shortfall : styles.sufficient}>
                        <td>{ingredient.name}</td>
                        <td>{Number(ingredient.displayRequired).toFixed(2)} {ingredient.displayRequiredUnit}</td>
                        <td>{Number(ingredient.displayStock).toFixed(2)} {ingredient.displayStockUnit}</td>
                        <td>
                          {isShort ? (
                            <span className={styles.statusShort}>
                              Short by {getBestUnit(ingredient.totalRequired - ingredient.currentStock, ingredient.unit).value.toFixed(2)} {getBestUnit(ingredient.totalRequired - ingredient.currentStock, ingredient.unit).unit}
                            </span>
                          ) : (
                            <span className={styles.statusSufficient}>Sufficient</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Order List Modal */}
      {showOrderList && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Shopping List - Items to Restock</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowOrderList(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {shortfallItems.length === 0 ? (
                <p>All ingredients are in stock! No items need to be purchased.</p>
              ) : (
                <>
                  <table className={styles.orderTable}>
                    <thead>
                      <tr>
                        <th>Ingredient</th>
                        <th>Current Stock</th>
                        <th>Need to Buy</th>
                        <th>Used In</th>
                        <th>Unit Cost</th>
                        <th>Estimated Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shortfallItems.map(item => {
                        // Find which recipes use this ingredient
                        const usedInRecipes = selectedRecipes.filter(recipe => 
                          recipe.recipe.some(ingredient => 
                            (ingredients.find(ing => ing.id === ingredient.ingredientId)?.name || '') === item.name
                          )
                        ).map(recipe => recipe.productName);

                        return (
                          <tr key={item.id}>
                            <td>
                              <strong>{item.name}</strong>
                            </td>
                            <td>
                              {item.currentStock} {item.unit}
                            </td>
                            <td>
                              <span className={styles.shortfallAmount}>
                                {item.shortfallAmount} {item.shortfallUnit || item.unit}
                              </span>
                            </td>
                            <td>
                              <div className={styles.recipeList}>
                                {usedInRecipes.map((recipeName, index) => (
                                  <span key={index} className={styles.recipeTag}>
                                    {recipeName}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td>${item.cost.toFixed(2)}</td>
                            <td>
                              <strong>${item.estimatedCost.toFixed(2)}</strong>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className={styles.totalCost}>
                    <strong>
                      Total Estimated Cost: ${shortfallItems.reduce((sum, item) => sum + item.estimatedCost, 0).toFixed(2)}
                    </strong>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionDashboard;
